import type { ExtractionDetailDto, ExtractionVersionDetailDto } from '@sara/api-client';
import { Button } from '@sara/ui/components/button';
import { Plus } from 'lucide-react';
import { PageHeader } from '../../../../components/page-header';

interface ExtractionDetailProps {
  extraction: ExtractionDetailDto;
  onCreateVersion: () => void;
}

function CodeBlock({ content }: { content: string }) {
  return (
    <pre className="overflow-x-auto rounded-xl border border-border/70 bg-card px-5 py-4 font-mono text-[12.5px] leading-relaxed text-foreground/90 whitespace-pre-wrap break-words">
      {content}
    </pre>
  );
}

export function ExtractionDetail({ extraction, onCreateVersion }: ExtractionDetailProps) {
  const latestVersion: ExtractionVersionDetailDto | undefined = extraction.versions[0];
  const pillButtonClass =
    'h-8 px-3 text-xs [&_svg]:size-3.5 sm:h-9 sm:px-4 sm:text-sm sm:[&_svg]:size-4';

  return (
    <div className="space-y-12">
      <PageHeader
        size="compact"
        title={extraction.name}
        description={extraction.description ?? undefined}
        meta={
          latestVersion ? (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
              <span className="h-1 w-1 rounded-full bg-primary" aria-hidden />
              <span className="tabular-nums">v{latestVersion.version}</span>
            </span>
          ) : null
        }
        action={
          <Button
            variant="outline"
            shape="pill"
            size="sm"
            className={pillButtonClass}
            onClick={onCreateVersion}
          >
            <Plus strokeWidth={1.6} />
            New version
          </Button>
        }
      />

      {latestVersion && (
        <>
          <section className="space-y-5">
            <h2 className="font-display text-2xl font-medium tracking-tight text-foreground">
              Prompt
            </h2>
            <CodeBlock content={latestVersion.prompt} />
          </section>

          <section className="space-y-5">
            <h2 className="font-display text-2xl font-medium tracking-tight text-foreground">
              Output schema
            </h2>
            <CodeBlock content={JSON.stringify(latestVersion.outputSchema, null, 2)} />
          </section>
        </>
      )}

      <section className="space-y-5">
        <h2 className="font-display text-2xl font-medium tracking-tight text-foreground">
          Versions
        </h2>
        {extraction.versions.length === 0 ? (
          <p className="text-sm text-muted-foreground">No versions have been created yet.</p>
        ) : (
          <ul className="divide-y divide-border/70 border-y border-border/70">
            {extraction.versions.map((version) => {
              const isLatest = version.id === latestVersion?.id;
              return (
                <li
                  key={version.id}
                  className="flex items-center justify-between gap-6 py-4 text-sm"
                >
                  <div className="flex items-baseline gap-3">
                    <span className="font-display text-base text-foreground tabular-nums">
                      v{version.version}
                    </span>
                    {isLatest && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/60 px-2 py-0.5 text-[11px] font-medium text-accent-foreground">
                        <span className="h-1 w-1 rounded-full bg-primary" aria-hidden />
                        Latest
                      </span>
                    )}
                  </div>
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {new Date(version.createdAt).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
